/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {Component, Vue} from 'vue-property-decorator';
import {CommunityChallengeInterface} from '@/store/modules/community-challenge/types';
import {Getter} from 'vuex-class';

/**
 * Community challenge active item mixin.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-community-challenge-active-item',
})
export default class CommunityChallengeActiveItem extends Vue {
    @Getter('challenge', {namespace: 'communityChallengeModule'})
    public challenge!: CommunityChallengeInterface;

    @Getter('count', {namespace: 'communityChallengeModule'})
    public count!: number;

    @Getter('title', {namespace: 'communityChallengeModule'})
    public title!: string;

    @Getter('synopsis', {namespace: 'communityChallengeModule'})
    public synopsis!: string;

    @Getter('description', {namespace: 'communityChallengeModule'})
    public description!: string;

    /**
     * Get total - adjust if personal total is greater than cc total.
     * @return {number}
     */
    public get total(): number {
        return this.challenge.total < (this.count || 0) ? this.count : this.challenge.total;
    }

    /**
     * Getter for completion percentage.
     * @return {number}
     */
    public get percentageComplete(): number {
        const pc: number = (this.total / this.challenge.quantity) * 100;
        if (pc > 100) {
            return 100;
        }

        if (pc < 0) {
            return 0;
        }
        return Math.round(pc);
    }

    /**
     * Getter to ensure challenge is active.
     * @return {boolean}
     */
    public get active(): boolean {
        return (this.challenge.expiresAtTimestamp - this.challenge.serverTimestamp) > 0;
    }

    /**
     * Determine the remaining time.
     * @return {string}
     */
    public get timeLeft(): string {
        let expiry: number = this.challenge.expiresAtTimestamp - this.challenge.serverTimestamp;

        if (expiry < 0) {
            expiry = 0;
        }

        const days: number = Math.floor(expiry / (3600 * 24));
        if (days > 0) {
            return this.$i18n.translate('time.left.days', '', {days}, days) || '';
        }

        const hours: number = Math.floor(expiry / 3600);
        if (hours > 0) {
            return this.$i18n.translate('time.left.hours', '', {hours}, hours) || '';
        }

        const minutes: number = Math.floor((expiry - (hours * 3600)) / 60);
        return this.$i18n.translate('time.left.minutes', '', {minutes}, minutes) || '';
    }
}
